import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import {
  doc, getDoc, addDoc, updateDoc,
  collection, serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconArrowLeft,
  IconCheck,
  IconAlertTriangle,
} from '@tabler/icons-react';

const RECORD_TYPES = [
  { value: 'service',    labelKey: 'service.type_service',    descriptionKey: 'service.type_service_desc'    },
  { value: 'repair',     labelKey: 'service.type_repair',     descriptionKey: 'service.type_repair_desc'     },
  { value: 'inspection', labelKey: 'service.type_inspection', descriptionKey: 'service.type_inspection_desc' },
];

const PERFORMED_BY_TYPES = [
  { value: 'internal',  labelKey: 'service.by_internal' },
  { value: 'dealer',    labelKey: 'service.by_dealer'   },
  { value: 'external',  labelKey: 'service.by_external' },
];

export default function ServiceRecordNew() {
  const { t } = useTranslation();
  const { machineId } = useParams();
  const navigate = useNavigate();
  const { orgId, userProfile } = useAuth();

  const [machine, setMachine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Form fields
  const [type, setType] = useState('service');
  const [description, setDescription] = useState('');
  const [dateOfService, setDateOfService] = useState(new Date().toISOString().split('T')[0]);
  const [hoursAtService, setHoursAtService] = useState('');
  const [performedBy, setPerformedBy] = useState('');
  const [performedByType, setPerformedByType] = useState('internal');
  const [cost, setCost] = useState('');
  const [parts, setParts] = useState([{ name: '', quantity: 1, cost: '' }]);
  const [nextServiceHours, setNextServiceHours] = useState('');
  const [nextServiceDate, setNextServiceDate] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!orgId || !machineId) return;
      try {
        const snap = await getDoc(doc(db, 'organisations', orgId, 'machines', machineId));
        if (snap.exists()) {
          const data = { id: snap.id, ...snap.data() };
          setMachine(data);
          setHoursAtService(data.currentHours?.toString() || '');
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId, machineId]);

  const addPart = () => setParts(prev => [...prev, { name: '', quantity: 1, cost: '' }]);
  const removePart = (i) => setParts(prev => prev.filter((_, idx) => idx !== i));
  const updatePart = (i, field, value) => {
    setParts(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: value } : p));
  };

  const totalCost = () => {
    const partsCost = parts.reduce((sum, p) => sum + (Number(p.cost) * Number(p.quantity) || 0), 0);
    return (partsCost + (Number(cost) || 0)).toFixed(2);
  };

  const handleSave = async () => {
    if (!description.trim()) {
      setError(t('service.err_description'));
      return;
    }
    if (!dateOfService) {
      setError(t('service.err_date'));
      return;
    }

    setSaving(true);
    setError('');

    try {
      const record = {
        type,
        description: description.trim(),
        dateOfService,
        hoursAtService: hoursAtService ? Number(hoursAtService) : null,
        performedBy: performedBy.trim() || userProfile?.name || null,
        performedByType,
        cost: cost ? Number(cost) : null,
        parts: parts.filter(p => p.name.trim()),
        nextServiceHours: nextServiceHours ? Number(nextServiceHours) : null,
        nextServiceDate: nextServiceDate || null,
        notes: notes.trim() || null,
        sealed: true,
        sealedAt: serverTimestamp(),
        sealedBy: userProfile?.name || null,
        sealedById: userProfile?.id || null,
        createdAt: serverTimestamp(),
        createdBy: userProfile?.id || null,
      };

      // Add service record
      await addDoc(
        collection(db, 'organisations', orgId, 'machines', machineId, 'serviceRecords'),
        record
      );

      // Update machine hours and service schedule
      const updates = { updatedAt: serverTimestamp() };
      if (hoursAtService) updates.currentHours = Number(hoursAtService);
      if (nextServiceHours) updates.nextServiceHours = Number(nextServiceHours);
      if (nextServiceDate) updates.nextServiceDate = nextServiceDate;
      if (dateOfService) {
        updates.lastServiceDate = dateOfService;
        updates.lastServiceHours = hoursAtService ? Number(hoursAtService) : machine.currentHours;
      }

      // Update cost tracking
      const costField = type === 'repair' ? 'totalRepairCost' : 'totalServiceCost';
      const currentCost = machine.costTracking?.[costField] || 0;
      updates[`costTracking.${costField}`] = currentCost + Number(totalCost());

      await updateDoc(doc(db, 'organisations', orgId, 'machines', machineId), updates);

      toast.success(t('service.added_toast'));
      navigate(`/machines/${machineId}`);
    } catch (err) {
      console.error(err);
      setError(t('service.err_save'));
    }
    setSaving(false);
  };

  if (loading) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>
        {t('common.loading')}
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 680, margin: '0 auto' }}>

        {/* Back */}
        <div
          onClick={() => navigate(`/machines/${machineId}`)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}
        >
          <IconArrowLeft size={15} stroke={1.5} />
          {machine?.makeLabel} {machine?.modelLabel}
        </div>

        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>{t('service.add_record')}</h1>
          <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconAlertTriangle size={13} stroke={1.5} color="var(--color-warning)" />
            {t('service.seal_note')}
          </div>
        </div>

        {error && (
          <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 13, color: 'var(--color-danger)', marginBottom: 16 }}>
            {error}
          </div>
        )}

        {/* Record type */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('service.record_type')}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {RECORD_TYPES.map(rt => (
              <div
                key={rt.value}
                onClick={() => setType(rt.value)}
                style={{
                  flex: 1, padding: '12px 14px', borderRadius: 'var(--radius-md)',
                  border: '0.5px solid',
                  borderColor: type === rt.value ? 'var(--color-brand)' : 'var(--color-border-light)',
                  background: type === rt.value ? 'var(--color-brand-light)' : 'transparent',
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                <div style={{ fontSize: 13, color: type === rt.value ? 'var(--color-brand)' : 'var(--color-text-primary)', marginBottom: 2 }}>
                  {t(rt.labelKey)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{t(rt.descriptionKey)}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Details */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('staff.tab_details')}</div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
              {t('defects.col_description')} <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            <textarea
              placeholder={t('service.description_placeholder')}
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                {t('service.date_of_service')} <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <input
                type="date"
                value={dateOfService}
                onChange={e => setDateOfService(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                {t('service.hours_at_service')}
              </label>
              <input
                type="number"
                placeholder={machine?.currentHours?.toString() || '0'}
                value={hoursAtService}
                onChange={e => setHoursAtService(e.target.value)}
                min={0}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
              {t('service.performed_by')}
            </label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              {PERFORMED_BY_TYPES.map(pt => (
                <div
                  key={pt.value}
                  onClick={() => setPerformedByType(pt.value)}
                  style={{
                    padding: '5px 12px', borderRadius: 'var(--radius-md)',
                    border: '0.5px solid',
                    borderColor: performedByType === pt.value ? 'var(--color-brand)' : 'var(--color-border-medium)',
                    background: performedByType === pt.value ? 'var(--color-brand-light)' : 'transparent',
                    color: performedByType === pt.value ? 'var(--color-brand)' : 'var(--color-text-secondary)',
                    fontSize: 12, cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  {t(pt.labelKey)}
                </div>
              ))}
            </div>
            <input
              type="text"
              placeholder={t('service.performed_by_placeholder')}
              value={performedBy}
              onChange={e => setPerformedBy(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
              {t('service.additional_notes')}
            </label>
            <textarea
              placeholder={t('service.notes_placeholder')}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>
        </Card>

        {/* Parts */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 400 }}>{t('service.parts_used')}</div>
            <button
              onClick={addPart}
              style={{ fontSize: 12, color: 'var(--color-brand)', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              + {t('service.add_part')}
            </button>
          </div>

          {parts.map((part, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 80px 100px 32px', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <input
                type="text"
                placeholder={t('service.part_name')}
                value={part.name}
                onChange={e => updatePart(i, 'name', e.target.value)}
                style={{ boxSizing: 'border-box' }}
              />
              <input
                type="number"
                placeholder={t('service.col_qty')}
                value={part.quantity}
                onChange={e => updatePart(i, 'quantity', e.target.value)}
                min={1}
                style={{ boxSizing: 'border-box' }}
              />
              <input
                type="number"
                placeholder={t('service.cost_placeholder')}
                value={part.cost}
                onChange={e => updatePart(i, 'cost', e.target.value)}
                min={0}
                style={{ boxSizing: 'border-box' }}
              />
              <button
                onClick={() => removePart(i)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-tertiary)', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ×
              </button>
            </div>
          ))}
        </Card>

        {/* Cost & next service */}
        <Card style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('service.cost_next_service')}</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                {t('service.labour_cost_field')}
              </label>
              <input
                type="number"
                placeholder="0.00"
                value={cost}
                onChange={e => setCost(e.target.value)}
                min={0}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 4 }}>{t('service.total_cost')}</div>
                <div style={{ fontSize: 20, fontWeight: 400, color: 'var(--color-text-primary)' }}>
                  £{totalCost()}
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                {t('service.next_service_hours')}
              </label>
              <input
                type="number"
                placeholder={t('service.next_hours_placeholder')}
                value={nextServiceHours}
                onChange={e => setNextServiceHours(e.target.value)}
                min={0}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                {t('service.next_service_date')}
              </label>
              <input
                type="date"
                value={nextServiceDate}
                onChange={e => setNextServiceDate(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
          </div>
        </Card>

        {/* Seal warning */}
        <div style={{ background: 'var(--color-warning-light)', border: '0.5px solid var(--color-warning)', borderRadius: 'var(--radius-md)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <IconAlertTriangle size={16} color="var(--color-warning)" stroke={1.5} />
          <div style={{ fontSize: 13, color: 'var(--color-warning)', lineHeight: 1.5 }}>
            {t('service.seal_warning')}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={() => navigate(`/machines/${machineId}`)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            fullWidth
            onClick={handleSave}
            disabled={saving}
            loading={saving}
            icon={<IconCheck size={14} stroke={1.5} />}
          >
            {saving ? t('common.saving') : t('service.save_seal')}
          </Button>
        </div>

      </div>
    </Layout>
  );
}